import { useState, useEffect, useCallback, useMemo } from 'react';
import { crmService } from '../../services/crmService';

const STAGE_COLORS = {
  identify: '#6B7280', qualify: '#3B82F6', develop: '#8B5CF6',
  capture: '#F59E0B', propose: '#EA580C', negotiate: '#EF4444',
  won: '#10B981', lost: '#374151',
};

const formatM = (v) => {
  if (!v) return '$0';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
};

function statusColor(cov) {
  if (!cov) return '#1E2D45';
  if (cov.subsidiaries.length > 0 && cov.pipeline_total > 0) return '#22D3EE';
  if (cov.subsidiaries.length > 0) return '#F59E0B';
  return '#1E2D45';
}

export default function VerticalCoverage() {
  const [verticals, setVerticals] = useState([]);
  const [coverage, setCoverage] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [allSubsidiaries, setAllSubsidiaries] = useState([]);
  const [addingTo, setAddingTo] = useState(null);
  const [selectedSubId, setSelectedSubId] = useState('');
  const [futureMode, setFutureMode] = useState(false);
  const [projection, setProjection] = useState(null);

  const enterFutureMode = () => {
    setFutureMode(true);
    if (!projection) {
      crmService.getVerticalProjection().then(setProjection).catch(console.error);
    }
  };

  // Group in-flight growth actions + watched deal flow by vertical
  const projByVertical = useMemo(() => {
    const map = {};
    if (!projection) return map;
    for (const a of projection.actions || []) {
      (map[a.vertical_id] = map[a.vertical_id] || { actions: [], watchlist: [] }).actions.push(a);
    }
    for (const w of projection.watchlist || []) {
      (map[w.vertical_id] = map[w.vertical_id] || { actions: [], watchlist: [] }).watchlist.push(w);
    }
    return map;
  }, [projection]);

  const isWhiteSpace = (v) => !(coverage[v.id]?.subsidiaries.length) && !(coverage[v.id]?.pipeline_total);
  const hasSources = (v) => {
    const p = projByVertical[v.id];
    return !!p && (p.actions.length + p.watchlist.length) > 0;
  };

  const loadAllCoverage = useCallback(async (verticalList) => {
    const results = await Promise.all(
      verticalList.map(v => crmService.getVerticalCoverage(v.id).catch(() => null))
    );
    const map = {};
    results.forEach((r, i) => { if (r) map[verticalList[i].id] = r; });
    setCoverage(map);
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [verts, subs] = await Promise.all([
          crmService.getVerticals(),
          crmService.getSubsidiaries(),
        ]);
        setVerticals(verts);
        setAllSubsidiaries(subs);
        await loadAllCoverage(verts);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [loadAllCoverage]);

  const refreshVertical = async (verticalId) => {
    try {
      const updated = await crmService.getVerticalCoverage(verticalId);
      setCoverage(prev => ({ ...prev, [verticalId]: updated }));
    } catch (err) { console.error(err); }
  };

  const handleAddSub = async (verticalId) => {
    if (!selectedSubId) return;
    try {
      await crmService.addSubsidiaryVertical(parseInt(selectedSubId), verticalId);
      await refreshVertical(verticalId);
      setAddingTo(null);
      setSelectedSubId('');
    } catch (err) { console.error(err); }
  };

  const handleRemoveSub = async (verticalId, subsidiaryId) => {
    try {
      await crmService.removeSubsidiaryVertical(subsidiaryId, verticalId);
      await refreshVertical(verticalId);
    } catch (err) { console.error(err); }
  };

  const covered   = verticals.filter(v => (coverage[v.id]?.subsidiaries.length || 0) > 0).length;
  const withPipeline = verticals.filter(v => (coverage[v.id]?.pipeline_total || 0) > 0).length;
  const whiteSpace   = verticals.filter(isWhiteSpace).length;
  const projectedCount = futureMode ? verticals.filter(v => isWhiteSpace(v) && hasSources(v)).length : 0;

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', fontSize: '13px' }}>
      Loading coverage data…
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Summary pills */}
      <div style={{
        display: 'flex', gap: '8px', padding: '10px 16px',
        backgroundColor: '#111827', borderBottom: '1px solid #1E2D45',
        alignItems: 'center', flexShrink: 0, flexWrap: 'wrap',
      }}>
        <span style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: '4px' }}>Our Territory — Customer Tech Gap Coverage</span>
        <span style={{
          background: '#22D3EE18', color: '#22D3EE', border: '1px solid #22D3EE44',
          padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
        }}>{covered} verticals covered</span>
        <span style={{
          background: '#F59E0B18', color: '#F59E0B', border: '1px solid #F59E0B44',
          padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
        }}>{withPipeline} with pipeline</span>
        <span style={{
          background: '#EF444418', color: '#EF4444', border: '1px solid #EF444444',
          padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
        }}>{futureMode ? `${whiteSpace - projectedCount} still white space` : `${whiteSpace} white space`}</span>
        {futureMode && (
          <span style={{
            background: '#c4a56318', color: '#c4a563', border: '1px solid #c4a56344',
            padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
          }}>{projectedCount} projected to close</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
          <button onClick={() => setFutureMode(false)} style={{
            padding: '3px 12px', borderRadius: '10px', fontSize: '10px', cursor: 'pointer', fontWeight: !futureMode ? 600 : 400,
            background: !futureMode ? '#22D3EE18' : 'transparent',
            color: !futureMode ? '#22D3EE' : '#6B7280',
            border: `1px solid ${!futureMode ? '#22D3EE44' : '#1E2D45'}`,
          }}>Current</button>
          <button onClick={enterFutureMode} style={{
            padding: '3px 12px', borderRadius: '10px', fontSize: '10px', cursor: 'pointer', fontWeight: futureMode ? 600 : 400,
            background: futureMode ? '#c4a56318' : 'transparent',
            color: futureMode ? '#c4a563' : '#6B7280',
            border: `1px solid ${futureMode ? '#c4a56344' : '#1E2D45'}`,
          }}>Future State</button>
          <span style={{ color: '#4B5563', fontSize: '10px', fontStyle: 'italic' }}>
            {futureMode ? 'Gold = gap closed by in-flight M&A or watched deal flow' : 'White space = M&A / BD gap signal'}
          </span>
        </div>
      </div>

      {/* Column headers */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 220px 60px 96px',
        padding: '5px 16px 5px 19px',
        backgroundColor: '#0A0F1C', borderBottom: '1px solid #111827', flexShrink: 0,
      }}>
        {['Vertical / Customer Problem', 'Subsidiaries', 'Opps', 'Pipeline'].map(h => (
          <div key={h} style={{ color: '#4B5563', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
        ))}
      </div>

      {/* Rows */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {verticals.map(v => {
          const cov = coverage[v.id];
          const projected = futureMode && isWhiteSpace(v) && hasSources(v);
          const border = projected ? '#c4a563' : statusColor(cov);
          const sources = projByVertical[v.id];
          const isExpanded = expandedId === v.id;
          const taggedSubs = cov?.subsidiaries || [];
          const allOpps = cov?.opportunities || [];
          const activeOpps = allOpps.filter(o => !['won', 'lost'].includes(o.stage));
          const untaggedSubs = allSubsidiaries.filter(s => !taggedSubs.some(ts => ts.id === s.id));

          return (
            <div key={v.id} style={{ borderBottom: '1px solid #111827' }}>

              {/* Main row */}
              <div
                onClick={() => setExpandedId(isExpanded ? null : v.id)}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 220px 60px 96px',
                  padding: '9px 16px 9px 0',
                  borderLeft: `3px solid ${border}`,
                  backgroundColor: isExpanded ? '#0D1B2A' : 'transparent',
                  cursor: 'pointer', transition: 'background-color 0.1s',
                }}
                onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#0C1624'; }}
                onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {/* Vertical name + problem */}
                <div style={{ paddingLeft: '13px', paddingRight: '12px' }}>
                  <div style={{ color: '#F1F5F9', fontSize: '12px', fontWeight: '600', marginBottom: '2px' }}>
                    {v.name}
                    {projected && (
                      <span style={{
                        marginLeft: '8px', background: '#c4a56318', color: '#c4a563', border: '1px solid #c4a56344',
                        padding: '1px 7px', borderRadius: '8px', fontSize: '9px', fontWeight: '700', verticalAlign: 'middle',
                      }}>
                        PROJECTED · {(sources?.actions.length || 0) + (sources?.watchlist.length || 0)} source{(sources?.actions.length || 0) + (sources?.watchlist.length || 0) !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#6B7280', fontSize: '10px', lineHeight: '1.4' }}>
                    {v.customer_problem}
                  </div>
                </div>

                {/* Subsidiaries */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'flex-start', paddingRight: '8px' }}>
                  {taggedSubs.length > 0
                    ? taggedSubs.map(s => (
                        <span key={s.id} style={{
                          background: '#22D3EE14', color: '#22D3EE', border: '1px solid #22D3EE30',
                          padding: '1px 7px', borderRadius: '9px', fontSize: '10px', fontWeight: '500',
                        }}>{s.name}</span>
                      ))
                    : <span style={{ color: '#EF4444', fontSize: '10px', opacity: 0.6 }}>None</span>
                  }
                </div>

                {/* Opp count */}
                <div style={{
                  color: activeOpps.length > 0 ? '#F1F5F9' : '#374151',
                  fontSize: '12px', fontWeight: activeOpps.length > 0 ? '600' : '400',
                  paddingTop: '1px',
                }}>
                  {activeOpps.length > 0 ? activeOpps.length : '—'}
                </div>

                {/* Pipeline */}
                <div style={{
                  color: cov?.pipeline_total > 0 ? '#22D3EE' : '#374151',
                  fontSize: '12px', fontWeight: '600', paddingTop: '1px',
                }}>
                  {cov?.pipeline_total > 0 ? formatM(cov.pipeline_total) : '—'}
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{
                  backgroundColor: '#0D1B2A', borderLeft: `3px solid ${border}`,
                  padding: '14px 16px 16px 16px',
                }}>
                  {/* Future-state sources */}
                  {futureMode && sources && (sources.actions.length > 0 || sources.watchlist.length > 0) && (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ color: '#c4a563', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                        Future State Sources
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {sources.actions.map(a => (
                          <span key={`a${a.id}`} title={`${a.stage} · ${a.status}`} style={{
                            display: 'inline-flex', gap: '6px', alignItems: 'baseline',
                            background: '#c4a56310', border: '1px solid #c4a56344', borderRadius: '9px',
                            padding: '3px 10px', fontSize: '10px',
                          }}>
                            <span style={{ color: '#c4a563', fontWeight: '700' }}>{a.type === 'ma' ? 'M&A' : a.type.toUpperCase()}</span>
                            <span style={{ color: '#F1F5F9' }}>{a.name}</span>
                            <span style={{ color: '#6B7280' }}>{a.stage}</span>
                          </span>
                        ))}
                        {sources.watchlist.map(w => (
                          <span key={`w${w.id}`} title={`status: ${w.status}`} style={{
                            display: 'inline-flex', gap: '6px', alignItems: 'baseline',
                            background: '#22D3EE10', border: '1px solid #22D3EE33', borderRadius: '9px',
                            padding: '3px 10px', fontSize: '10px',
                          }}>
                            <span style={{ color: '#22D3EE', fontWeight: '700' }}>DEAL FLOW</span>
                            <span style={{ color: '#F1F5F9' }}>{w.company_name}</span>
                            {w.ma_potential != null && <span style={{ color: '#6B7280' }}>{'★'.repeat(w.ma_potential)}</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '20px' }}>

                    {/* Subsidiaries panel */}
                    <div>
                      <div style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                        Subsidiaries
                      </div>
                      {taggedSubs.length === 0 && (
                        <div style={{ color: '#4B5563', fontSize: '11px', marginBottom: '8px' }}>None tagged</div>
                      )}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: taggedSubs.length ? '10px' : '0' }}>
                        {taggedSubs.map(s => (
                          <span key={s.id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            background: '#22D3EE14', color: '#22D3EE', border: '1px solid #22D3EE30',
                            padding: '2px 8px', borderRadius: '9px', fontSize: '11px',
                          }}>
                            {s.name}
                            <button
                              onClick={e => { e.stopPropagation(); handleRemoveSub(v.id, s.id); }}
                              style={{ background: 'none', border: 'none', color: '#22D3EE', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '0 0 0 2px', opacity: 0.6 }}
                            >×</button>
                          </span>
                        ))}
                      </div>

                      {addingTo === v.id ? (
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                          <select
                            value={selectedSubId}
                            onChange={e => setSelectedSubId(e.target.value)}
                            style={{ flex: 1, background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '4px 6px', borderRadius: '4px', fontSize: '11px' }}
                          >
                            <option value="">Select subsidiary…</option>
                            {untaggedSubs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                          <button
                            onClick={() => handleAddSub(v.id)}
                            disabled={!selectedSubId}
                            style={{
                              padding: '4px 10px',
                              background: selectedSubId ? '#22D3EE' : '#1E2D45',
                              color: selectedSubId ? '#0A0F1C' : '#4B5563',
                              border: 'none', borderRadius: '4px',
                              cursor: selectedSubId ? 'pointer' : 'default',
                              fontSize: '11px', fontWeight: 'bold',
                            }}
                          >Add</button>
                          <button
                            onClick={() => { setAddingTo(null); setSelectedSubId(''); }}
                            style={{ padding: '4px 8px', background: 'none', border: '1px solid #1E2D45', color: '#6B7280', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                          >✕</button>
                        </div>
                      ) : (
                        <button
                          onClick={e => { e.stopPropagation(); setAddingTo(v.id); setSelectedSubId(''); }}
                          style={{ padding: '3px 10px', background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', marginTop: '4px' }}
                        >+ Tag Subsidiary</button>
                      )}
                    </div>

                    {/* Opportunities panel */}
                    <div>
                      <div style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                        Active Opportunities
                      </div>
                      {activeOpps.length === 0 ? (
                        <div style={{ color: '#4B5563', fontSize: '11px' }}>None</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {activeOpps.map(o => (
                            <div key={o.id} style={{
                              display: 'flex', alignItems: 'center', gap: '8px',
                              background: '#111827', borderRadius: '4px', padding: '5px 8px',
                              borderLeft: `2px solid ${STAGE_COLORS[o.stage] || '#374151'}`,
                            }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ color: '#F1F5F9', fontSize: '11px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {o.name}
                                </div>
                                {o.subsidiary_name && (
                                  <div style={{ color: '#4B5563', fontSize: '9px' }}>{o.subsidiary_name}</div>
                                )}
                              </div>
                              <span style={{ color: STAGE_COLORS[o.stage] || '#6B7280', fontSize: '9px', textTransform: 'capitalize', flexShrink: 0 }}>
                                {o.stage}
                              </span>
                              {o.value_usd && (
                                <span style={{ color: '#22D3EE', fontSize: '11px', fontWeight: '600', flexShrink: 0 }}>
                                  {formatM(parseInt(o.value_usd))}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Key buyers panel */}
                    <div>
                      <div style={{ color: '#6B7280', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                        Key Buyers
                      </div>
                      <div style={{ color: '#94A3B8', fontSize: '11px', lineHeight: '1.7' }}>
                        {v.key_buyers}
                      </div>
                    </div>

                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
